import { t as msg, countText, formatNumber, currentLocale } from "../lib/i18n";
import { cardStatusText, friendlyDate } from "../lib/api";
import type { Card, Template } from "../../../../packages/shared/src/types";
import { Art } from "./Art";

export function CardPreview({ card, art, details = false }: {
  card: Partial<Card> & Partial<Pick<Template, "defaultTitle" | "defaultPromise" | "defaultUses">>;
  art?: string;
  details?: boolean;
}) {
  const inferred = art || ["coffee", "dish", "movie", "sleep", "breakfast"].find(x => (card.templateId || card.id || "").includes(x)) || "heart";
  const kind = inferred === "dish" ? "dishes" : inferred === "sleep" ? "moon" : inferred === "breakfast" ? "sun" : inferred;
  const received = card.viewer === "receiver" || card.viewer === "recipient";
  const recordedAt = received ? card.acceptedAt || card.deliveredAt || card.sentAt : card.sentAt;
  const uses = card.usesLeft !== undefined
    ? msg("{left} of {total} uses left", { left: formatNumber(card.usesLeft), total: formatNumber(card.usesTotal || 1) })
    : countText("usesCount", card.usesTotal || card.defaultUses || 1);
  return (
    <div className="card-shell"><article className={`promise-card theme-${card.theme || "sage"}`}>
      <div className="card-heading">
        {card.status ? <span className="card-state">{cardStatusText({status:card.status,moderation:card.moderation || "ok",deliveryMode:card.deliveryMode,deliveredAt:card.deliveredAt})}</span> : <span className="card-brand">{"a promise card"}</span>}
        <span className="use-stamp">{uses}</span>
      </div>
      <div className="card-message">
        <div>
          <h2>{card.title || card.defaultTitle || msg("Your lovely little promise")}</h2>
          <p className="promise-copy">{card.promise || card.defaultPromise || (!card.status ? msg("Something thoughtful. Something only you can give.") : "")}</p>
        </div>
        <Art kind={kind} />
      </div>
      {(card.signature || card.recipient?.displayName || card.recipientName || recordedAt) && <div className="card-bottom">
        <span>{card.viewer === "sender" && (card.recipient?.displayName || card.recipientName) ? msg("To {p0}", {p0:card.recipient?.displayName || card.recipientName || ""}) : card.signature ? msg("From {p0}", {p0:card.signature}) : ""}</span>
        {recordedAt && <time className="card-date" dateTime={recordedAt} aria-label={friendlyDate(recordedAt)}>{new Intl.DateTimeFormat(currentLocale(), {month:"short",day:"numeric"}).format(new Date(recordedAt))}</time>}
      </div>}
    </article>
    {details && <CardWriting card={card} />}
    </div>
  );
}

export function CardWriting({ card }: {card: Partial<Card> & Partial<Pick<Template, "defaultTitle" | "defaultPromise">>}) {
  return <details className="card-writing card-disclosure">
    <summary>{msg("Card details")}</summary>
    <h3>{card.title || card.defaultTitle}</h3>
    <p>{card.promise || card.defaultPromise}</p>
    {card.note && <blockquote className="personal-note">{card.note}</blockquote>}
    <dl className="card-facts">
      {card.signature && <><dt>{msg("From")}</dt><dd>{card.signature}</dd></>}
      {(card.recipient?.displayName || card.recipientName) && <><dt>{msg("To")}</dt><dd>{card.recipient?.displayName || card.recipientName}</dd></>}
      {card.sentAt && <><dt>{msg("Sent")}</dt><dd>{friendlyDate(card.sentAt)}</dd></>}
      {card.acceptedAt && <><dt>{msg("Accepted")}</dt><dd>{friendlyDate(card.acceptedAt)}</dd></>}
      {card.unlockAt && <><dt>{msg("Opens")}</dt><dd>{friendlyDate(card.unlockAt)}</dd></>}
      {card.expiresAt && <><dt>{msg("Use before")}</dt><dd>{friendlyDate(card.expiresAt)}</dd></>}
    </dl>
  </details>;
}
